import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Shield, Globe, Database, ArrowRight, Network,
  Linkedin, Twitter, Target, MapPin,
  Github, Send, Check, Search, Eye,
  Code, Mail, Users, Plane, Camera,
  Bot, Zap, Terminal, Key, Wallet,
  ChevronDown, Building2, Scale, Newspaper, Briefcase, Banknote, Landmark
} from 'lucide-react'
import OsintPreviewWidget from '@/components/OsintPreviewWidget'
import LinkAnalysisAnimation from '@/components/LinkAnalysisAnimation'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import SubscriptionTiers from '@/components/SubscriptionTiers'
import CookieConsent from '@/components/CookieConsent'
import { SEOHead } from '@/components/SEOHead'

const industryNav = [
  { slug: 'government', name: 'Government & Defense', icon: Building2, freeAccess: false },
  { slug: 'law-enforcement', name: 'Law Enforcement', icon: Shield, freeAccess: false },
  { slug: 'financial-services', name: 'Financial Services', icon: Landmark, freeAccess: false },
  { slug: 'cybersecurity', name: 'Cybersecurity Teams & SOCs', icon: Target, freeAccess: false },
  { slug: 'legal', name: 'Legal & Compliance', icon: Scale, freeAccess: false },
  { slug: 'journalism', name: 'Journalism & Media', icon: Newspaper, freeAccess: false },
  { slug: 'corporate-risk', name: 'Corporate Risk', icon: Briefcase, freeAccess: false },
  { slug: 'aml', name: 'Anti-Money Laundering', icon: Banknote, freeAccess: false },
  { slug: 'private-investigators', name: 'Private Investigators', icon: Search, freeAccess: false },
]

const industryDetails: Record<string, string> = {
  government: 'Strategic intelligence for national security and defense agencies.',
  'law-enforcement': 'Digital forensics and investigation tools for police and cybercrime units.',
  'financial-services': 'Fraud detection, counterparty risk, and financial crime intelligence.',
  cybersecurity: 'IOC enrichment, threat actor profiling, and real-time SOC monitoring.',
  legal: 'Digital evidence gathering and due diligence for legal professionals.',
  journalism: 'Source verification and disinformation investigation for reporters.',
  'corporate-risk': 'Executive protection, third-party risk, and insider threat monitoring.',
  aml: 'Beneficial ownership, transaction mapping, and PEP screening.',
  'private-investigators': 'Full digital footprint analysis and identity intelligence.',
}

export default function LandingNew() {
  const [email, setEmail] = useState('')
  const [subscribed, setSubscribed] = useState(false)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [showPartnershipForm, setShowPartnershipForm] = useState(false)
  const [partnershipData, setPartnershipData] = useState({
    name: '',
    email: '',
    company: '',
    partnershipType: '',
    message: ''
  })
  const [partnershipSubmitted, setPartnershipSubmitted] = useState(false)

  // Track mouse for parallax effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY })
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // TODO: Integrate with newsletter API
    setSubscribed(true)
    setTimeout(() => setSubscribed(false), 3000)
  }

  const handlePartnershipSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const { apiClient } = await import('@/lib/api')
      const response = await apiClient.submitPartnershipRequest({
        name: partnershipData.name,
        email: partnershipData.email,
        company: partnershipData.company,
        partnership_type: partnershipData.partnershipType,
        message: partnershipData.message || undefined
      })
      
      if (response.success) {
        setPartnershipSubmitted(true)
        setTimeout(() => {
          setPartnershipSubmitted(false)
          setShowPartnershipForm(false)
          setPartnershipData({ name: '', email: '', company: '', partnershipType: '', message: '' })
        }, 3000)
      }
    } catch (error: any) {
      console.error('Failed to submit partnership request:', error)
      alert(error.message || 'Failed to submit partnership request. Please try again.')
    }
  }

  return (
    <>
      <SEOHead
        title="Sycek OSINT - Next-Gen Cyber Intelligence & OSINT Platform | Threat Detection & Investigation"
        description="Enterprise-grade OSINT platform with real-time threat monitoring, breach intelligence, geospatial analysis, social media investigation, and AI-powered cyber intelligence. Built for security professionals, analysts, and investigators."
        keywords="OSINT platform, cyber intelligence, threat intelligence, breach detection, data leak intelligence, GEOINT, SOCINT, social media investigation, digital forensics, cybersecurity tools, threat monitoring, security investigation, identity correlation, maritime intelligence, flight surveillance, facial recognition, OSINT tools, open source intelligence, threat hunting, incident response, vulnerability assessment, DDoS monitoring, ransomware tracking, sanctions screening"
        canonical="/"
        jsonLd={{
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is Sycek OSINT?",
              "acceptedAnswer": { "@type": "Answer", "text": "Sycek is an enterprise-grade open source intelligence (OSINT) platform that provides real-time threat monitoring, breach intelligence, geospatial analysis, social media investigation, and AI-powered cyber intelligence across multiple domains including cyber, geopolitical, financial, and infrastructure." }
            },
            {
              "@type": "Question",
              "name": "What intelligence domains does Sycek cover?",
              "acceptedAnswer": { "@type": "Answer", "text": "Sycek covers cyber threats (CVEs, ransomware, DDoS, IOCs), geopolitical events (conflicts, sanctions, protests), infrastructure (flights, maritime AIS, outages), social media intelligence (Twitter/X investigation, Telegram), financial markets (stocks, crypto, prediction markets), and natural events (earthquakes, wildfires)." }
            },
            {
              "@type": "Question",
              "name": "Who is Sycek designed for?",
              "acceptedAnswer": { "@type": "Answer", "text": "Sycek is built for security professionals, threat intelligence analysts, OSINT investigators, law enforcement, corporate security teams, and researchers who need comprehensive real-time intelligence across multiple domains." }
            },
            {
              "@type": "Question",
              "name": "Does Sycek offer real-time threat monitoring?",
              "acceptedAnswer": { "@type": "Answer", "text": "Yes. The Live Monitor dashboard provides real-time global threat visualization on an interactive 3D globe with WebSocket-powered live event streaming, escalation scoring, and multi-domain correlation signals." }
            }
          ]
        }}
      />
      
      <div className="min-h-screen bg-[#0a0f0a] text-white relative overflow-hidden">
        {/* Animated Hexagonal Grid Background with Parallax */}
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `
              linear-gradient(rgba(34, 197, 94, 0.2) 1px, transparent 1px),
              linear-gradient(90deg, rgba(34, 197, 94, 0.2) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
            animation: 'gridMove 20s linear infinite',
            transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.01}px)`
          }}
        />
        
        {/* Animated Scan Lines */}
        <div className="absolute inset-0 pointer-events-none opacity-10">
          {[...Array(3)].map((_, i) => (
            <div 
              key={i}
              className="absolute inset-0 bg-gradient-to-b from-transparent via-green-500/30 to-transparent h-0.5 animate-scan"
              style={{ 
                top: `${30 + i * 25}%`,
                animationDelay: `${i * 1}s`,
                animationDuration: '4s'
              }} 
            />
          ))}
        </div>
        
        {/* Matrix-style Data Streams */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute w-0.5 h-32 bg-gradient-to-b from-green-500/40 via-green-400/60 to-transparent animate-dataStream"
              style={{
                left: `${15 + i * 12}%`,
                top: '-128px',
                animationDelay: `${i * 0.6}s`,
                animationDuration: `${5 + i * 0.3}s`
              }}
            />
          ))}
        </div>
        
        {/* Floating OSINT Icons with Animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[Search, Target, Shield, Network, Eye, Code, Database, Globe, MapPin].map((Icon, i) => (
            <div
              key={i}
              className="absolute animate-float"
              style={{
                left: `${10 + (i * 12)}%`,
                top: `${15 + (i * 8)}%`,
                animationDelay: `${i * 0.5}s`,
                animationDuration: `${6 + (i % 3)}s`
              }}
            >
              <Icon
                className="text-green-500/10 blur-sm"
                style={{
                  width: `${40 + (i % 3) * 20}px`,
                  height: `${40 + (i % 3) * 20}px`,
                }}
              />
            </div>
          ))}
        </div>

        {/* Animated Data Streams */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute w-0.5 h-20 bg-gradient-to-b from-green-500/30 to-transparent animate-dataStream"
              style={{
                left: `${20 + i * 20}%`,
                top: '-80px',
                animationDelay: `${i * 0.8}s`,
                animationDuration: `${4 + i * 0.5}s`
              }}
            />
          ))}
        </div>

        {/* Glowing Orbs with Movement */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-green-500/5 rounded-full blur-3xl animate-pulse animate-float" style={{ animationDuration: '8s' }} />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-500/5 rounded-full blur-3xl animate-pulse animate-float" style={{ animationDelay: '1s', animationDuration: '10s' }} />
          <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-green-500/3 rounded-full blur-3xl animate-pulse animate-float" style={{ animationDelay: '2s', animationDuration: '12s' }} />
          <div className="absolute top-1/4 right-1/4 w-48 h-48 bg-red-500/3 rounded-full blur-3xl animate-pulse animate-float" style={{ animationDelay: '3s', animationDuration: '9s' }} />
        </div>
        
        {/* Radial Gradient Overlays */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-gradient-radial from-green-500/5 via-transparent to-transparent" />
          <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-radial from-red-500/5 via-transparent to-transparent" />
        </div>

        {/* Header */}
        <header className="sticky top-0 z-50 border-b border-white/[0.07] bg-[#070b0e]/95 backdrop-blur-md">
          <div className="container mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity flex-shrink-0">
              <div className="w-9 h-9 rounded-lg overflow-hidden">
                <img src="/logo.jpeg" alt="Sycek OSINT" className="w-full h-full object-cover" />
              </div>
              <div>
                <h1 className="text-base font-bold text-white tracking-tight leading-none">Sycek</h1>
                <p className="text-[10px] text-slate-500 uppercase tracking-widest leading-none mt-0.5">Intelligence Platform</p>
              </div>
            </Link>

            {/* Enterprise Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {/* Industries dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setOpenDropdown('industries')}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button className="flex items-center gap-1.5 px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors rounded-md hover:bg-white/[0.06]">
                  Industries
                  <ChevronDown className={`w-3.5 h-3.5 opacity-60 transition-transform duration-200 ${openDropdown === 'industries' ? 'rotate-180' : ''}`} />
                </button>
                {openDropdown === 'industries' && (
                  <div className="absolute top-full left-0 mt-1 w-72 bg-[#0d1117] border border-white/[0.09] rounded-xl shadow-2xl shadow-black/70 p-2 z-50">
                    {industryNav.map(ind => (
                      <Link
                        key={ind.slug}
                        to={`/industries/${ind.slug}`}
                        className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-white/[0.05] text-slate-400 hover:text-white transition-colors group"
                      >
                        <div className="flex items-center gap-3">
                          <ind.icon className="w-4 h-4 text-slate-600 group-hover:text-green-400 transition-colors flex-shrink-0" />
                          <span className="text-sm">{ind.name}</span>
                        </div>
                        {ind.freeAccess && (
                          <span className="text-[10px] bg-green-500/15 text-green-400 border border-green-500/25 rounded px-1.5 py-0.5 flex-shrink-0">Free</span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Products dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setOpenDropdown('products')}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button className="flex items-center gap-1.5 px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors rounded-md hover:bg-white/[0.06]">
                  Products
                  <ChevronDown className={`w-3.5 h-3.5 opacity-60 transition-transform duration-200 ${openDropdown === 'products' ? 'rotate-180' : ''}`} />
                </button>
                {openDropdown === 'products' && (
                  <div className="absolute top-full left-0 mt-1 w-60 bg-[#0d1117] border border-white/[0.09] rounded-xl shadow-2xl shadow-black/70 p-2 z-50">
                    {[
                      { name: 'BreachINT', desc: 'Breach & credential leak intel', icon: Database, to: '/breachint' },
                      { name: 'Sycek Profiler', desc: 'Identity intelligence', icon: Users, to: '/sycek-profiler' },
                      { name: 'WalletINT', desc: 'Crypto wallet AML screening', icon: Wallet, to: '/walletint' },
                      { name: 'AML Profiler', desc: 'Customer screening & EDD', icon: Banknote, to: '/aml-profiler' },
                      { name: 'SOCINT', desc: 'Cross-platform identity graph', icon: Search, to: '/socint' },
                      { name: 'Social Stream', desc: 'Multi-platform monitoring', icon: Network, to: '/social-stream-product' },
                      { name: 'GEOINT', desc: 'Satellite & geospatial', icon: Globe, to: '/geoint' },
                      { name: 'X Investigation', desc: 'Twitter deep-dive', icon: Twitter, to: '/x-investigation-product' },
                      { name: 'Air & Maritime', desc: 'Flight & vessel tracking', icon: Plane, to: '/air-maritime' },
                    ].map(p => (
                      <Link key={p.name} to={p.to} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.05] text-slate-400 hover:text-white transition-colors group">
                        <p.icon className="w-4 h-4 text-slate-600 group-hover:text-green-400 transition-colors flex-shrink-0" />
                        <div>
                          <div className="text-sm">{p.name}</div>
                          <div className="text-[11px] text-slate-600">{p.desc}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link to="/use-cases" className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors rounded-md hover:bg-white/[0.06]">
                Use Cases
              </Link>
              <Link to="/monitor" className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors rounded-md hover:bg-white/[0.06]">
                Live Monitor
              </Link>
              <a href="#pricing" className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors rounded-md hover:bg-white/[0.06]">
                Pricing
              </a>
              <Link to="/training" className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors rounded-md hover:bg-white/[0.06]">
                Training
              </Link>
            </nav>

            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-white/[0.06]">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-green-500 hover:bg-green-600 text-black font-semibold shadow-lg shadow-green-500/30">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="relative z-10 min-h-[90vh] flex items-center">
          <div className="container mx-auto px-4 py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
              {/* Left Side - Content */}
              <div className="space-y-8">
                <div>
                  <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                    <span className="text-white">Enterprise-Grade</span>
                    <br />
                    <span className="text-green-400 drop-shadow-[0_0_30px_rgba(34,197,94,0.8)]">
                     OSINT Intelligence Platform
                    </span>
                  </h1>

                  <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
                    Built by a team with 6+ years running live cyber intelligence operations — Sycek turns hours of manual OSINT work into minutes of structured, actionable insight.
                  </p>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    size="lg"
                    className="bg-green-500 hover:bg-green-600 text-black font-semibold text-lg px-8 py-6 shadow-lg shadow-green-500/50"
                    onClick={() => document.getElementById('exposure-check')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Try For Yourself
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>

                {/* Trust Indicators */}
                <div className="flex items-center gap-6 pt-4 flex-wrap">
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="w-10 h-10 rounded-full bg-green-500/20 border-2 border-green-500/50" />
                    ))}
                  </div>
                  <p className="text-gray-400 text-sm">
                    Trusted by <span className="text-green-400 font-semibold">1,200+</span> intelligence professionals
                  </p>
                  <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-full px-4 py-1.5">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-xs text-slate-400">6+ years operator experience</span>
                  </div>
                </div>
              </div>

              {/* Right Side - Terminal/Dashboard Preview */}
              <div className="relative">
                <div className="relative bg-black/50 border border-green-500/30 rounded-lg p-6 backdrop-blur-sm shadow-2xl shadow-green-500/20">
                  {/* Terminal Window Header */}
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-green-500/20">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500/50" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                      <div className="w-3 h-3 rounded-full bg-green-500/50" />
                    </div>
                    <span className="text-xs text-green-400/70 font-mono ml-4">$ sycek_intelligence.js</span>
                  </div>

                  {/* Terminal Content */}
                  <div className="space-y-4 font-mono text-sm">
                    <div className="text-green-400">
                      <span className="text-green-500">[SYCEK@ROOT]#</span> initialize_intelligence_engine
                    </div>
                    <div className="text-gray-400 pl-4">
                      ✓ Loading breach databases...<br />
                      ✓ Connecting to GEOINT services...<br />
                      ✓ Initializing AI analysis...
                    </div>
                    
                    {/* Target Analytics Panel */}
                    <div className="mt-6 p-4 bg-green-500/5 border border-green-500/20 rounded">
                      <div className="text-green-400 mb-2 font-semibold">Target Analytics</div>
                      <div className="flex items-center justify-center h-32">
                        <div className="relative w-24 h-24">
                          <div className="absolute inset-0 border-2 border-green-500/50 rounded-full animate-pulse" />
                          <div className="absolute inset-4 border-2 border-green-400/30 rounded-full" />
                          <div className="absolute inset-8 border-2 border-green-300/20 rounded-full" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-ping" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Data Collection Panel */}
                    <div className="p-4 bg-green-500/5 border border-green-500/20 rounded">
                      <div className="text-green-400 mb-2 font-semibold">Data Collection</div>
                      <div className="space-y-2">
                        {[75, 60, 45, 30].map((width, i) => (
                          <div key={i} className="h-2 bg-green-500/20 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full transition-all duration-1000"
                              style={{ width: `${width}%` }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute -bottom-8 right-0 flex items-center gap-2 text-green-400/50 text-sm">
                  <span>Scroll to explore</span>
                  <div className="w-6 h-10 border-2 border-green-500/30 rounded-full flex items-start justify-center p-2">
                    <div className="w-1 h-3 bg-green-500/50 rounded-full animate-bounce" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Exposure Check Section */}
        <section id="exposure-check" className="relative z-10 border-t border-white/[0.06] overflow-hidden">
          {/* Background layers */}
          <div className="absolute inset-0 bg-gradient-to-b from-black via-red-950/10 to-black pointer-events-none" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(239,68,68,0.07),transparent)] pointer-events-none" />

          <div className="relative z-10 container mx-auto px-4">

            {/* Stats bar */}
            <div className="border-b border-white/[0.06] py-5">
              <div className="flex flex-wrap justify-center gap-10 md:gap-20">
                {[
                  { value: '18B+', label: 'Records indexed' },
                  { value: '47,000+', label: 'Breach sources' },
                  { value: '212', label: 'Countries tracked' },
                  { value: '2.4M+', label: 'Threats/day' },
                ].map(stat => (
                  <div key={stat.label} className="flex items-baseline gap-2">
                    <div className="text-xl md:text-2xl font-bold text-red-400 font-mono tabular-nums">{stat.value}</div>
                    <div className="text-[10px] text-gray-700 uppercase tracking-widest font-mono">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Main content */}
            <div className="py-16 md:py-20">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-red-500/8 border border-red-500/20 px-4 py-1.5 text-[10px] text-red-500 font-bold uppercase tracking-[0.2em] mb-6 font-mono">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse inline-block" />
                  Free Preview — No account required
                </div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
                  Your data is already<br />
                  <span className="text-red-400">out there.</span>{' '}
                  <span className="text-gray-400">Find it first.</span>
                </h2>
                <p className="text-gray-400 max-w-2xl mx-auto text-lg leading-relaxed">
                  Hackers don't break in. They log in — using credentials from breaches you don't know about.
                  Search 18 billion records, investigate social profiles, and monitor narratives. No account needed to start.
                </p>
              </div>

              {/* Capability matrix */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-800/30 border border-gray-800/50 max-w-4xl mx-auto mb-12 overflow-hidden">
                {[
                  {
                    num: '01',
                    title: 'BREACH INTELLIGENCE',
                    accent: '#ef4444',
                    sub: '18B+ records · 47,000+ sources',
                    points: [
                      'Email exposure across breach databases',
                      'Credential & stealer log correlation',
                      'Data type classification & severity rating',
                    ],
                  },
                  {
                    num: '02',
                    title: 'X / TWITTER INTEL',
                    accent: '#38bdf8',
                    sub: 'Any public profile · Real-time',
                    points: [
                      'Full profile & follower network mapping',
                      'Tweet activity timeline & engagement score',
                      'Bot probability & coordinated behaviour flags',
                    ],
                  },
                  {
                    num: '03',
                    title: 'SOCIAL LISTENING',
                    accent: '#10b981',
                    sub: 'Cross-platform · Any keyword',
                    points: [
                      'Real-time keyword & hashtag monitoring',
                      'Cross-platform sentiment breakdown',
                      'Narrative cluster & influence mapping',
                    ],
                  },
                ].map(item => (
                  <div key={item.num} className="bg-[#07070d] p-6 hover:bg-[#0a0a12] transition-colors group">
                    <div className="flex items-start gap-3 mb-5">
                      <span className="text-[10px] font-mono text-gray-700 mt-1 select-none">{item.num}</span>
                      <div>
                        <div className="text-[11px] font-bold tracking-widest uppercase mb-1" style={{ color: item.accent }}>
                          {item.title}
                        </div>
                        <div className="text-[10px] text-gray-700 font-mono">{item.sub}</div>
                        <div className="h-px w-10 mt-2" style={{ background: item.accent + '35' }} />
                      </div>
                    </div>
                    <ul className="space-y-2.5">
                      {item.points.map(pt => (
                        <li key={pt} className="flex items-start gap-2">
                          <span className="text-[10px] font-mono mt-0.5 select-none" style={{ color: item.accent + '80' }}>›</span>
                          <span className="text-xs text-gray-600 leading-snug group-hover:text-gray-500 transition-colors">{pt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* The widget */}
              <OsintPreviewWidget />
            </div>
          </div>
        </section>

        {/* Ecosystem & Operator Credibility */}
        <section className="relative z-10 border-t border-white/[0.06] border-b border-white/[0.06] bg-black/40 py-16">
          <div className="container mx-auto px-6">
            <div className="text-center mb-10">
              <p className="text-slate-500 text-xs uppercase tracking-widest font-mono mb-3">Built by operators, not just engineers</p>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                6+ years of intelligence operations behind the platform
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Sycek is the product of a team that has built and operated multiple cyber intelligence platforms — accumulating real-world threat intelligence infrastructure used by thousands of security professionals worldwide.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-3xl mx-auto mb-12">
              {[
                { name: 'Cyber Intelligence', desc: '6+ years building live threat intelligence pipelines for enterprise & government', icon: Shield, color: '#ef4444' },
                { name: 'OSINT Operations', desc: 'Operators who ran real-world investigations before writing a line of platform code', icon: Search, color: '#3b82f6' },
                { name: 'Platform Engineering', desc: 'Scalable, API-first architecture designed for analyst workflows — not demos', icon: Code, color: '#22c55e' },
              ].map(p => (
                <div
                  key={p.name}
                  className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-5 flex items-center gap-4 hover:border-white/15 hover:bg-white/[0.05] transition-all group"
                >
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${p.color}18`, border: `1px solid ${p.color}40` }}>
                    <p.icon className="w-5 h-5" style={{ color: p.color }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-white font-semibold text-sm group-hover:text-green-400 transition-colors">{p.name}</div>
                    <div className="text-slate-500 text-xs leading-snug mt-0.5">{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {[
                { value: '6+', label: 'Years Operating' },
                { value: '3', label: 'Intel Companies' },
                { value: '50K+', label: 'Professionals Served' },
                { value: '190+', label: 'Countries Covered' },
              ].map(stat => (
                <div key={stat.label} className="text-center bg-white/[0.03] border border-white/[0.06] rounded-xl py-5 px-3">
                  <div className="text-2xl font-bold text-green-400 font-mono mb-1">{stat.value}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries We Serve */}
        <section className="relative z-10 py-24 border-b border-white/[0.06]">
          <div className="container mx-auto px-6">
            <motion.div
              className="text-center mb-14"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-4 bg-blue-500/15 text-blue-400 border border-blue-500/30 font-mono">
                Industries We Serve
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="text-white">Purpose-built for</span>
                <br />
                <span className="text-blue-400">every sector</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Tailored intelligence workflows and capabilities for the specific operational needs of your industry.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
              {industryNav.map((ind, index) => (
                <motion.div
                  key={ind.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: (index % 3) * 0.08 }}
                >
                  <Link
                    to={`/industries/${ind.slug}`}
                    className="flex items-start gap-4 p-5 bg-white/[0.03] border border-white/[0.07] rounded-xl hover:border-white/15 hover:bg-white/[0.05] transition-all group block"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/20 transition-colors">
                      <ind.icon className="w-5 h-5 text-blue-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-semibold text-sm group-hover:text-blue-400 transition-colors">{ind.name}</h3>
                        {ind.freeAccess && (
                          <span className="text-[10px] bg-green-500/15 text-green-400 border border-green-500/25 rounded px-1.5 py-0.5 flex-shrink-0">Free</span>
                        )}
                      </div>
                      <p className="text-slate-500 text-xs leading-relaxed">{industryDetails[ind.slug]}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-0.5" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="relative z-10 py-24 bg-[#0a0f0a]">
          <div className="container mx-auto px-4">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-4 bg-green-500/20 text-green-400 border border-green-500/50 font-mono">
                [INTELLIGENCE MODULES]
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="text-white">Powerful Intelligence</span>
                <br />
                <span className="text-green-400">Capabilities</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                7 specialized intelligence modules. One unified platform. From identity profiling to satellite imagery — all connected by AI.
              </p>
            </motion.div>

            {/* Stats strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-16">
              {([
                { value: '40B+', label: 'Breach Records Indexed' },
                { value: '8+', label: 'Social Platforms Monitored' },
                { value: '70+', label: 'Ransomware Groups Tracked' },
                { value: '30+', label: 'Live Intelligence Sources' },
              ] as Array<{ value: string; label: string }>).map((stat, i) => (
                <motion.div
                  key={stat.label}
                  className="text-center bg-green-500/5 border border-green-500/20 rounded-xl py-5 px-4 hover:border-green-500/40 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <div className="text-3xl font-bold text-green-400 font-mono mb-1">{stat.value}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">{stat.label}</div>
                </motion.div>
              ))}
            </div>

            {/* Intelligence Graph showcase */}
            <motion.div
              className="max-w-5xl mx-auto mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="border border-green-500/20 rounded-2xl overflow-hidden bg-black/60">
                {/* Terminal-style header bar */}
                <div className="border-b border-green-500/20 px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500/50" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                      <div className="w-3 h-3 rounded-full bg-green-500/50" />
                    </div>
                    <span className="text-xs text-green-400/70 font-mono">intelligence_graph.live — cross-module entity correlation</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-[10px] text-green-400/60 font-mono">LIVE</span>
                  </div>
                </div>
                {/* Canvas */}
                <div className="h-[360px]">
                  <LinkAnalysisAnimation />
                </div>
                {/* Legend */}
                <div className="border-t border-green-500/20 px-5 py-3 flex flex-wrap gap-5 text-xs font-mono text-gray-500">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-400 inline-block flex-shrink-0" />
                    Target entities
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-green-400 inline-block flex-shrink-0" />
                    Linked profiles
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 inline-block flex-shrink-0" />
                    Social accounts
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-400 inline-block flex-shrink-0" />
                    Infrastructure nodes
                  </span>
                </div>
              </div>
              <p className="text-center text-xs text-gray-600 mt-3 font-mono">
                Sycek correlates identities, breach data, social accounts, and infrastructure across all modules in real time
              </p>
            </motion.div>

            {/* Module Grid — 7 modules */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-7xl mx-auto">
              {([
                {
                  icon: Shield,
                  title: 'Sycek Profiler',
                  tagline: 'From any identifier to a complete intelligence profile',
                  color: '#ef4444',
                  features: [
                    'Search by email, phone, username, full name, Gov ID, or domain',
                    'Phone → caller ID & linked social account discovery',
                    'PEP screening, sanctions & watchlist checks',
                    'Company corruption & criminal exposure analysis',
                    'Breach history & credential exposure scoring',
                    'Cross-platform identity correlation graph',
                  ],
                  useCase: 'Investigate a suspicious contact from their email address alone',
                },
                {
                  icon: Network,
                  title: 'Social Stream Intelligence',
                  tagline: 'Real-time monitoring across 8+ platforms simultaneously',
                  color: '#60a5fa',
                  features: [
                    'Facebook, X, Instagram, YouTube, Reddit, TikTok, Telegram',
                    'Keyword, hashtag & account surveillance',
                    'AI sentiment analysis & threat scoring',
                    'Real-time alerts & escalation triggers',
                    'Coordinated campaign detection',
                    'AI threat summaries & PDF report generation',
                  ],
                  useCase: 'Track a disinformation campaign as it spreads across platforms',
                },
                {
                  icon: Globe,
                  title: 'GEOINT',
                  tagline: 'Satellite imagery meets geospatial threat analysis',
                  color: '#22c55e',
                  features: [
                    'Before/after satellite imagery comparison',
                    'Area of Interest (AOI) management & alerts',
                    'Change detection with timestamps',
                    'Historical imagery timeline',
                    'Maritime AIS vessel tracking',
                    'Port & shipping route analysis',
                  ],
                  useCase: 'Detect unauthorized construction at a sensitive facility',
                },
                {
                  icon: Twitter,
                  title: 'X / Twitter Investigation',
                  tagline: 'Deep-dive profiling of any X account or event',
                  color: '#22d3ee',
                  features: [
                    'Followers & following network graph',
                    'Tweet pattern & behavior analysis',
                    'Keyword & hashtag timeline reconstruction',
                    'List membership & pull investigation',
                    'Post details, media & engagement data',
                    'AI-generated behavioral profile report',
                  ],
                  useCase: 'Map the influence network behind a coordinated tweet campaign',
                },
                {
                  icon: Users,
                  title: 'Identity Correlation',
                  tagline: 'Link identities across every digital touchpoint',
                  color: '#a78bfa',
                  features: [
                    'Cross-platform identity linking',
                    'Relationship & association graph mapping',
                    'Timeline reconstruction & activity analysis',
                    'Alias & pseudonym detection',
                    'Behavioral pattern recognition',
                    'Multi-source evidence aggregation',
                  ],
                  useCase: 'Trace an anonymous threat actor across multiple platforms',
                },
                {
                  icon: Camera,
                  title: 'Facial Recognition',
                  tagline: 'Reverse photo search across billions of social images',
                  color: '#f472b6',
                  features: [
                    'Reverse face search via Search4Faces',
                    'Match across VK, TikTok, Odnoklassniki',
                    'PimEyes deep-web face search',
                    'Identity verification from a single photo',
                    'Social media profile discovery',
                    'Multi-platform facial matching',
                  ],
                  useCase: 'Identify an unknown subject from a single photo',
                },
                {
                  icon: Plane,
                  title: 'Air & Maritime Tracking',
                  tagline: 'Real-time surveillance of aircraft and vessels worldwide',
                  color: '#f59e0b',
                  features: [
                    'Live flight tracking (OpenSky network)',
                    'Military vs. civilian aircraft classification',
                    'ATC audio monitoring & recording',
                    'AIS vessel tracking & voyage history',
                    'Port activity & cargo route analysis',
                    'Airport & fleet intelligence',
                  ],
                  useCase: 'Track a private jet or cargo vessel movement in real time',
                },
              ] as Array<{
                icon: any;
                title: string;
                tagline: string;
                color: string;
                features: string[];
                useCase: string;
              }>).map((mod, index) => (
                <motion.div
                  key={index}
                  className="relative bg-black/60 border border-green-500/10 rounded-xl overflow-hidden hover:border-green-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-black/60 group flex flex-col"
                  style={{ borderLeftColor: mod.color, borderLeftWidth: '3px' }}
                  initial={{ opacity: 0, y: 35 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.45, delay: (index % 3) * 0.1 }}
                  whileHover={{ scale: 1.015, transition: { duration: 0.2 } }}
                >
                  <div className="p-7 flex flex-col flex-grow">
                    <div className="flex items-start justify-between mb-5">
                      <div
                        className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${mod.color}18`, border: `1px solid ${mod.color}40` }}
                      >
                        <mod.icon className="w-5 h-5" style={{ color: mod.color }} />
                      </div>
                      <span className="text-xs font-mono text-gray-700 uppercase tracking-widest mt-1">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-1.5 group-hover:text-green-400 transition-colors">
                      {mod.title}
                    </h3>
                    <p className="text-sm font-medium mb-5" style={{ color: mod.color }}>
                      {mod.tagline}
                    </p>
                    <ul className="space-y-2.5 mb-6 flex-grow">
                      {mod.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-gray-300">
                          <span
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-[7px]"
                            style={{ backgroundColor: mod.color }}
                          />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <div className="border-t border-gray-800 pt-4 mt-auto">
                      <p className="text-xs text-gray-500">
                        <span className="text-gray-400 font-medium">Use case: </span>
                        {mod.useCase}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* MCP — AI-Native Intelligence Section */}
        <section className="relative z-10 py-20 bg-black/20 border-t border-purple-500/20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-purple-500/20 text-purple-400 border border-purple-500/50 font-mono">
                [MCP — AI-NATIVE]
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="text-white">Talk to your</span>
                <br />
                <span className="text-purple-400">Intelligence Platform</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Connect Claude, Cursor, or any AI assistant directly to Sycek OSINT via the Model Context Protocol.
                Pull live data from Facebook, X, Reddit, Instagram, YouTube, TikTok, forums, and the web — in plain English.
              </p>
            </div>

            <div className="max-w-5xl mx-auto">

              {/* Social platform icons row */}
              <div className="mb-10">
                <p className="text-center text-xs text-gray-500 font-mono uppercase tracking-widest mb-6">[Fetch intelligent data from 8+ platforms via a single prompt]</p>
                <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                  {/* Facebook */}
                  <div className="flex flex-col items-center gap-2 group">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center border border-[#1877F2]/30 bg-[#1877F2]/10 group-hover:bg-[#1877F2]/20 group-hover:border-[#1877F2]/60 transition-all duration-200">
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#1877F2">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </div>
                    <span className="text-[10px] text-gray-500">Facebook</span>
                  </div>
                  {/* X / Twitter */}
                  <div className="flex flex-col items-center gap-2 group">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center border border-gray-500/30 bg-gray-500/10 group-hover:bg-gray-500/20 group-hover:border-gray-400/50 transition-all duration-200">
                      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#e5e7eb">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </div>
                    <span className="text-[10px] text-gray-500">X / Twitter</span>
                  </div>
                  {/* Reddit */}
                  <div className="flex flex-col items-center gap-2 group">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center border border-[#FF4500]/30 bg-[#FF4500]/10 group-hover:bg-[#FF4500]/20 group-hover:border-[#FF4500]/60 transition-all duration-200">
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#FF4500">
                        <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                      </svg>
                    </div>
                    <span className="text-[10px] text-gray-500">Reddit</span>
                  </div>
                  {/* Instagram */}
                  <div className="flex flex-col items-center gap-2 group">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center border border-[#E1306C]/30 bg-[#E1306C]/10 group-hover:bg-[#E1306C]/20 group-hover:border-[#E1306C]/60 transition-all duration-200">
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#E1306C">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
                      </svg>
                    </div>
                    <span className="text-[10px] text-gray-500">Instagram</span>
                  </div>
                  {/* YouTube */}
                  <div className="flex flex-col items-center gap-2 group">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center border border-[#FF0000]/30 bg-[#FF0000]/10 group-hover:bg-[#FF0000]/20 group-hover:border-[#FF0000]/60 transition-all duration-200">
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#FF0000">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </div>
                    <span className="text-[10px] text-gray-500">YouTube</span>
                  </div>
                  {/* TikTok */}
                  <div className="flex flex-col items-center gap-2 group">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center border border-[#69C9D0]/30 bg-[#69C9D0]/10 group-hover:bg-[#69C9D0]/20 group-hover:border-[#69C9D0]/60 transition-all duration-200">
                      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#69C9D0">
                        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                      </svg>
                    </div>
                    <span className="text-[10px] text-gray-500">TikTok</span>
                  </div>
                  {/* Telegram */}
                  <div className="flex flex-col items-center gap-2 group">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center border border-[#2AABEE]/30 bg-[#2AABEE]/10 group-hover:bg-[#2AABEE]/20 group-hover:border-[#2AABEE]/60 transition-all duration-200">
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#2AABEE">
                        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                      </svg>
                    </div>
                    <span className="text-[10px] text-gray-500">Telegram</span>
                  </div>
                  {/* Forums / Web */}
                  <div className="flex flex-col items-center gap-2 group">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center border border-green-500/30 bg-green-500/10 group-hover:bg-green-500/20 group-hover:border-green-500/60 transition-all duration-200">
                      <Globe className="w-6 h-6 text-green-400" />
                    </div>
                    <span className="text-[10px] text-gray-500">Forums / Web</span>
                  </div>
                </div>
              </div>

              {/* Demo prompt bar */}
              <div className="bg-black/60 border border-purple-500/30 rounded-xl p-6 mb-10 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-900/10 via-transparent to-purple-900/10" />
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-4 text-xs text-gray-500 font-mono">
                    <Bot className="w-4 h-4 text-purple-400" />
                    <span>Claude Desktop + sycek-osint MCP</span>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/50 text-[10px]">LIVE</Badge>
                  </div>
                  <p className="text-gray-200 text-lg font-light italic mb-6">
                    "Fetch the last 500 posts mentioning 'Operation XYZ' across Facebook, Reddit, and Telegram, run sentiment analysis, surface the top threat actors, and export a STIX 2.1 bundle for Sentinel."
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                    <span className="bg-black/40 border border-purple-500/20 rounded px-2 py-1 font-mono">social_stream_search</span>
                    <span className="text-gray-600">→</span>
                    <span className="bg-black/40 border border-purple-500/20 rounded px-2 py-1 font-mono">sentiment_analysis</span>
                    <span className="text-gray-600">→</span>
                    <span className="bg-black/40 border border-purple-500/20 rounded px-2 py-1 font-mono">threat_actor_pivot</span>
                    <span className="text-gray-600">→</span>
                    <span className="bg-black/40 border border-purple-500/20 rounded px-2 py-1 font-mono">stix_export</span>
                    <span className="ml-2 text-purple-400 font-mono">4 tools · ~18 credits · 1 prompt</span>
                  </div>
                </div>
              </div>

              {/* Two options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                <Card className="bg-black/50 border border-purple-500/30 hover:border-purple-500/60 backdrop-blur-sm transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20 group">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center mb-4 group-hover:bg-purple-500/20 transition-colors text-purple-400">
                      <Zap className="w-6 h-6" />
                    </div>
                    <CardTitle className="text-white group-hover:text-purple-400 transition-colors flex items-center gap-2">
                      Zero-Install
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/50 text-[10px]">RECOMMENDED</Badge>
                    </CardTitle>
                    <CardDescription className="text-gray-400">Point your AI client directly at the Sycek cloud SSE endpoint. No Python, no setup.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-xs text-gray-300 font-mono bg-black/60 rounded-lg p-3 overflow-x-auto">{`"sycek-osint": {
  "type": "sse",
  "url": "https://sycek.io/mcp/sse",
  "headers": {
    "Authorization": "Bearer sk_..."
  }
}`}</pre>
                  </CardContent>
                </Card>

                <Card className="bg-black/50 border border-purple-500/30 hover:border-purple-500/60 backdrop-blur-sm transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20 group">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center mb-4 group-hover:bg-purple-500/20 transition-colors text-purple-400">
                      <Terminal className="w-6 h-6" />
                    </div>
                    <CardTitle className="text-white group-hover:text-purple-400 transition-colors">Local pip Install</CardTitle>
                    <CardDescription className="text-gray-400">Run the MCP server locally. Works offline once installed. Requires Python 3.10+.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-xs text-gray-300 font-mono bg-black/60 rounded-lg p-3 overflow-x-auto">{`pip install sycek-osint-mcp

"sycek-osint": {
  "command": "sycek-osint-mcp",
  "env": {
    "SYCEK_API_KEY": "sk_..."
  }
}`}</pre>
                  </CardContent>
                </Card>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-4 mb-10">
                {[
                  { value: '8+', label: 'Social Platforms', color: 'text-purple-400' },
                  { value: '20+', label: 'MCP Tools', color: 'text-blue-400' },
                  { value: '1', label: 'Prompt', color: 'text-green-400' },
                ].map(({ value, label, color }) => (
                  <div key={label} className="text-center bg-black/40 border border-purple-500/20 rounded-xl p-6">
                    <div className={`text-4xl font-bold font-mono ${color} mb-1`}>{value}</div>
                    <div className="text-gray-400 text-sm">{label}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link to="/api-docs">
                  <button className="bg-purple-500 hover:bg-purple-600 text-white font-semibold text-lg px-8 py-4 rounded-lg shadow-lg shadow-purple-500/30 flex items-center gap-2 transition-colors">
                    <Bot className="w-5 h-5" />
                    MCP Setup Guide
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </Link>
                <Link to="/app/developer">
                  <button className="border border-purple-500/50 text-purple-300 hover:bg-purple-500/10 font-semibold px-8 py-4 rounded-lg flex items-center gap-2 transition-colors">
                    <Key className="w-4 h-4" />
                    Get API Key
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Training Modules Section */}
        <section className="relative z-10 py-20 bg-black/20 border-t border-green-500/20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-green-500/20 text-green-400 border border-green-500/50 font-mono">
                [TRAINING PROGRAMS]
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="text-white">Elite Intelligence</span>
                <br />
                <span className="text-green-400">Training Programs</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Master advanced OSINT and cyber intelligence techniques through our comprehensive training modules designed for security professionals, analysts, and investigators.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
              {[
                { 
                  title: 'Advanced OSINT Techniques', 
                  desc: 'Master open source intelligence gathering and analysis methods',
                  icon: Search
                },
                { 
                  title: 'Cyber Intelligence Operations', 
                  desc: 'Learn threat detection, breach analysis, and digital forensics',
                  icon: Shield
                },
                { 
                  title: 'Geospatial Intelligence', 
                  desc: 'Satellite imagery analysis and location-based intelligence',
                  icon: Globe
                }
              ].map((program, index) => (
                <Card key={index} className="bg-black/50 border border-green-500/20 hover:border-green-500/50 backdrop-blur-sm transition-all duration-300 hover:shadow-lg hover:shadow-green-500/20 group">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center mb-4 group-hover:bg-green-500/20 transition-colors text-green-400">
                      <program.icon className="w-6 h-6" />
                    </div>
                    <CardTitle className="text-white group-hover:text-green-400 transition-colors">{program.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-400">{program.desc}</CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Link to="/training">
                <Button size="lg" className="bg-green-500 hover:bg-green-600 text-black font-semibold text-lg px-8 py-6 shadow-lg shadow-green-500/50">
                  Explore All Training Programs
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* ── Clients / Trusted By ───────────────────────────────────────────── */}
        <section className="relative z-10 py-20 border-t border-white/[0.06] bg-black/30">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-slate-500/15 text-slate-400 border border-slate-500/30 font-mono text-[10px] tracking-widest">
                [TRUSTED BY]
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                Used by security teams across sectors
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto text-sm">
                From financial institutions to government agencies — Sycek powers intelligence workflows for professionals who can't afford to be wrong.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl mx-auto mb-10">
              {[
                { name: 'Meridian Capital Partners', sector: 'Financial Services' },
                { name: 'Atlas Security Group', sector: 'Cybersecurity' },
                { name: 'Crestwood Advisory', sector: 'Risk & Compliance' },
                { name: 'Nexus Risk Consulting', sector: 'Corporate Risk' },
                { name: 'Vanguard Cyber Solutions', sector: 'SOC / MSSP' },
                { name: 'Pacific Rim Intelligence', sector: 'Government' },
                { name: 'Endeavor Legal Partners', sector: 'Legal & AML' },
                { name: 'Irongate Investigations', sector: 'Private Investigations' },
                { name: 'Sentinel Compliance Group', sector: 'Regulatory' },
                { name: 'Cobalt Intelligence Group', sector: 'Threat Intel' },
                { name: 'Arclight Forensics', sector: 'Digital Forensics' },
                { name: 'Orion Defence Analytics', sector: 'Defense' },
              ].map((c, i) => (
                <div key={i} className="bg-white/[0.02] border border-white/[0.07] rounded-xl px-4 py-4 text-center hover:border-white/15 hover:bg-white/[0.04] transition-all">
                  <div className="w-8 h-8 rounded-lg bg-green-500/10 border border-green-500/20 mx-auto mb-2 flex items-center justify-center">
                    <span className="text-green-400 font-bold text-xs">{c.name.substring(0, 2).toUpperCase()}</span>
                  </div>
                  <div className="text-white font-semibold text-xs leading-tight mb-1">{c.name}</div>
                  <div className="text-slate-600 text-[10px]">{c.sector}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-center gap-8 pt-4 border-t border-white/[0.04]">
              {[
                { value: '1,200+', label: 'Active Users' },
                { value: '40+', label: 'Enterprise Clients' },
                { value: '19', label: 'Countries Deployed' },
                { value: '99.7%', label: 'Platform Uptime' },
              ].map(s => (
                <div key={s.label} className="text-center">
                  <div className="text-2xl font-bold text-green-400 font-mono">{s.value}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── How Sycek Simplified OSINT ────────────────────────────────────────── */}
        <section className="relative z-10 py-24 border-t border-white/[0.06] bg-[#070b0e]">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-green-500/15 text-green-400 border border-green-500/30 font-mono text-[10px] tracking-widest">
                [THE SYCEK DIFFERENCE]
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                We wrote the complexity.<br />
                <span className="text-green-400">You just ask the question.</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                A complete OSINT investigation used to require 15+ specialized tools, hours of manual correlation, and deep technical expertise.
                Sycek collapses that into a single platform — with one unified data model, one API, and one result.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto mb-16">
              {/* Before */}
              <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                    <span className="text-red-400 font-bold text-sm">✕</span>
                  </div>
                  <h3 className="text-white font-bold text-lg">Before Sycek</h3>
                  <Badge className="ml-auto bg-red-500/15 text-red-400 border border-red-500/30 text-[10px]">Old Way</Badge>
                </div>
                <ul className="space-y-3">
                  {[
                    { tool: 'Maltego / i2', task: 'Entity link analysis' },
                    { tool: 'Shodan + Censys', task: 'Infrastructure pivoting' },
                    { tool: 'Have I Been Pwned', task: 'Breach lookups' },
                    { tool: 'Google Dorks', task: 'Surface web search' },
                    { tool: 'SpiderFoot', task: 'Automated recon' },
                    { tool: 'Babel Street', task: 'Social media OSINT' },
                    { tool: 'TGStat / Combot', task: 'Telegram monitoring' },
                    { tool: 'Paliscope / ENFI', task: 'Evidence management' },
                    { tool: 'Custom scripts', task: 'Glue everything together' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500/50 flex-shrink-0" />
                      <span className="text-red-400/80 font-mono text-xs w-32 flex-shrink-0">{item.tool}</span>
                      <span className="text-slate-500">{item.task}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-4 border-t border-red-500/20 grid grid-cols-3 gap-3 text-center">
                  <div><div className="text-red-400 font-bold font-mono">9+</div><div className="text-slate-600 text-[10px] uppercase">Tools</div></div>
                  <div><div className="text-red-400 font-bold font-mono">4–8 hrs</div><div className="text-slate-600 text-[10px] uppercase">Per investigation</div></div>
                  <div><div className="text-red-400 font-bold font-mono">Manual</div><div className="text-slate-600 text-[10px] uppercase">Correlation</div></div>
                </div>
              </div>

              {/* After */}
              <div className="bg-green-500/5 border border-green-500/20 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <span className="text-green-400 font-bold text-sm">✓</span>
                  </div>
                  <h3 className="text-white font-bold text-lg">With Sycek</h3>
                  <Badge className="ml-auto bg-green-500/15 text-green-400 border border-green-500/30 text-[10px]">Now</Badge>
                </div>
                <ul className="space-y-3">
                  {[
                    { step: 'Enter one identifier', detail: 'Email, phone, username, domain, or photo' },
                    { step: 'Automatic cross-module enrichment', detail: 'Breach records, social accounts, GEOINT, and IOCs pulled in parallel' },
                    { step: 'AI correlation engine runs', detail: 'Entities linked across 30+ data sources automatically' },
                    { step: 'Threat scoring applied', detail: 'Risk indicators, PEP flags, and sanction matches surfaced' },
                    { step: 'Graph visualization generated', detail: 'Relationship map built without any manual work' },
                    { step: 'Report ready', detail: 'PDF with full source citations and audit trail exported' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <div className="w-5 h-5 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-green-400 text-[10px] font-bold">{i + 1}</span>
                      </div>
                      <div>
                        <span className="text-green-300 font-medium">{item.step}</span>
                        <span className="text-slate-500 ml-2 text-xs">{item.detail}</span>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-4 border-t border-green-500/20 grid grid-cols-3 gap-3 text-center">
                  <div><div className="text-green-400 font-bold font-mono">1</div><div className="text-slate-500 text-[10px] uppercase">Platform</div></div>
                  <div><div className="text-green-400 font-bold font-mono">&lt;15 min</div><div className="text-slate-500 text-[10px] uppercase">Full profile</div></div>
                  <div><div className="text-green-400 font-bold font-mono">Auto</div><div className="text-slate-500 text-[10px] uppercase">Correlation</div></div>
                </div>
              </div>
            </div>

            {/* Pipeline stats */}
            <div className="max-w-4xl mx-auto bg-white/[0.02] border border-white/[0.07] rounded-2xl p-8">
              <h3 className="text-white font-bold text-lg mb-6 text-center">What runs under the hood on every query</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {[
                  { value: '30+', label: 'Data sources queried', color: '#22c55e' },
                  { value: '40B+', label: 'Breach records indexed', color: '#60a5fa' },
                  { value: '8+', label: 'Social platforms monitored', color: '#a78bfa' },
                  { value: '190+', label: 'Countries in coverage', color: '#f59e0b' },
                ].map((s, i) => (
                  <div key={i} className="text-center">
                    <div className="text-3xl font-black font-mono mb-1" style={{ color: s.color }}>{s.value}</div>
                    <div className="text-slate-500 text-xs uppercase tracking-wide">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── About Us ──────────────────────────────────────────────────────────── */}
        <section className="relative z-10 py-24 border-t border-white/[0.06] bg-black/40">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                {/* Left — story */}
                <div>
                  <Badge className="mb-5 bg-blue-500/15 text-blue-400 border border-blue-500/30 font-mono text-[10px] tracking-widest">
                    [ABOUT SYCEK]
                  </Badge>
                  <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                    Built by practitioners.<br />
                    <span className="text-blue-400">For practitioners.</span>
                  </h2>
                  <p className="text-slate-300 text-lg leading-relaxed mb-5">
                    Sycek is built by a team with a decade-long track record in cybersecurity assessment, threat intelligence, and digital forensics operations across India and internationally.
                  </p>
                  <p className="text-slate-400 leading-relaxed mb-5">
                    Our team has conducted hundreds of cybersecurity assessments for enterprises, financial institutions, and critical infrastructure operators. We've built and operated threat intelligence platforms, responded to live cyber incidents, and trained government and enterprise security teams across South and Southeast Asia.
                  </p>
                  <p className="text-slate-400 leading-relaxed mb-8">
                    Sycek wasn't designed in a product meeting. It was designed in investigation rooms — built to solve the exact problems our own analysts faced daily: too many tools, too much manual work, and too little time.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link to="/register">
                      <Button className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-5">
                        Start Using Sycek
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                    <a href="mailto:contact@sycek.io">
                      <Button variant="ghost" className="text-slate-300 hover:text-white border border-white/10 px-6 py-5">
                        Contact the Team
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Right — credentials */}
                <div className="space-y-4">
                  {[
                    {
                      year: 'JAN',
                      title: 'Platform Launch',
                      desc: 'Sycek launched with real-time threat monitoring, 3D globe dashboard, and live cyber intelligence ingestion across ransomware, CVEs, and DDoS feeds.',
                      color: '#22c55e',
                    },
                    {
                      year: 'FEB',
                      title: 'SOCINT & X Investigation',
                      desc: 'Deployed social graph search across emails, phones, and usernames. Launched deep X/Twitter investigation with timeline analysis and network mapping.',
                      color: '#60a5fa',
                    },
                    {
                      year: 'MAR',
                      title: 'BreachINT — 18B+ Records',
                      desc: 'Integrated access to 18 billion+ breach records across LeakOSINT, OCCRP Aleph, OpenSanctions, and Hudson Rock stealer log intelligence.',
                      color: '#a78bfa',
                    },
                    {
                      year: 'APR',
                      title: 'GEOINT & Air / Maritime',
                      desc: 'Added satellite imagery AOI management, live AIS vessel tracking, OpenSky flight intelligence, and military vs. civil aircraft tagging.',
                      color: '#f59e0b',
                    },
                    {
                      year: 'JUN',
                      title: 'AML Profiler & MCP',
                      desc: 'Shipped AML / EDD profiler with financial risk scoring, corporate registry lookups, and MCP integration exposing 20+ OSINT tools to AI agents.',
                      color: '#ef4444',
                    },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center font-mono text-xs font-bold" style={{ backgroundColor: `${item.color}18`, border: `1px solid ${item.color}40`, color: item.color }}>
                          {item.year}
                        </div>
                        {i < 4 && <div className="w-px flex-1 mt-1 min-h-[24px]" style={{ backgroundColor: `${item.color}20` }} />}
                      </div>
                      <div className="pb-4">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-slate-500">{item.year}</span>
                          <span className="text-white font-semibold text-sm">{item.title}</span>
                        </div>
                        <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Team expertise strips */}
              <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Cybersecurity Assessments', count: '500+', icon: Shield },
                  { label: 'Years in Threat Intelligence', count: '6+', icon: Target },
                  { label: 'Countries Served', count: '19', icon: Globe },
                  { label: 'Intelligence Modules Built', count: '12+', icon: Database },
                ].map((item, i) => {
                  const Icon = item.icon
                  return (
                    <div key={i} className="bg-white/[0.02] border border-white/[0.07] rounded-xl p-5 text-center">
                      <Icon className="w-5 h-5 text-green-400 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-white font-mono mb-0.5">{item.count}</div>
                      <div className="text-slate-500 text-xs">{item.label}</div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Intelligence Tiers Section */}
        <SubscriptionTiers />

        {/* Channel Partnerships Section */}
        <section className="relative z-10 py-20 bg-black/20 border-t border-green-500/20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-green-500/20 text-green-400 border border-green-500/50 font-mono">
                [PARTNERSHIPS]
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="text-white">Channel</span>
                <span className="text-green-400 ml-3">Partnerships</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Partner with Sycek to expand your cybersecurity offerings and deliver cutting-edge OSINT intelligence to your clients.
              </p>
            </div>

            {/* Current Partners Showcase */}
            <div className="mb-16">
              <h3 className="text-2xl font-bold text-white mb-3 text-center">Technology & Reseller Partners</h3>
              <p className="text-slate-400 text-sm text-center mb-8 max-w-xl mx-auto">We work with cybersecurity firms, MSSPs, and system integrators to deliver Sycek intelligence capabilities to their clients.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
                {[
                  { type: 'Technology', desc: 'Data intelligence feeds', color: '#ef4444' },
                  { type: 'Reseller', desc: 'Regional distribution', color: '#3b82f6' },
                  { type: 'Integration', desc: 'SIEM / SOAR connectors', color: '#22c55e' },
                  { type: 'Training', desc: 'OSINT training delivery', color: '#f59e0b' },
                ].map((p, i) => (
                  <div key={i} className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5 text-center hover:border-white/15 transition-all">
                    <div className="w-10 h-10 rounded-lg mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: `${p.color}18`, border: `1px solid ${p.color}30` }}>
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
                    </div>
                    <div className="text-white font-semibold text-sm mb-1">{p.type}</div>
                    <div className="text-slate-500 text-xs">{p.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Partnership Request Form */}
            <div className="max-w-2xl mx-auto">
              <Card className="bg-black/50 border border-green-500/30 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-green-400 text-2xl flex items-center">
                    <Users className="w-6 h-6 mr-2" />
                    Request Partnership
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Fill out the form below to explore partnership opportunities with Sycek OSINT.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => setShowPartnershipForm(true)}
                    className="w-full bg-green-500 hover:bg-green-600 text-black font-semibold"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Request Partnership
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Partnership Form Dialog */}
        <Dialog open={showPartnershipForm} onOpenChange={setShowPartnershipForm}>
          <DialogContent className="bg-black border border-green-500/30 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl text-green-400">Channel Partnership Request</DialogTitle>
              <DialogDescription className="text-gray-400">
                Tell us about your organization and partnership interests. We'll get back to you within 24 hours.
              </DialogDescription>
            </DialogHeader>
            
            {partnershipSubmitted ? (
              <div className="text-center py-8">
                <Check className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-green-400 mb-2">Thank You!</h3>
                <p className="text-gray-300">
                  We've received your partnership request. A confirmation email has been sent to your inbox.
                </p>
              </div>
            ) : (
              <form onSubmit={handlePartnershipSubmit} className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Full Name *
                  </label>
                  <Input
                    required
                    value={partnershipData.name}
                    onChange={(e) => setPartnershipData({ ...partnershipData, name: e.target.value })}
                    className="bg-black/50 border-green-500/30 text-white"
                    placeholder="John Doe"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    required
                    value={partnershipData.email}
                    onChange={(e) => setPartnershipData({ ...partnershipData, email: e.target.value })}
                    className="bg-black/50 border-green-500/30 text-white"
                    placeholder="john@company.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Company/Organization *
                  </label>
                  <Input
                    required
                    value={partnershipData.company}
                    onChange={(e) => setPartnershipData({ ...partnershipData, company: e.target.value })}
                    className="bg-black/50 border-green-500/30 text-white"
                    placeholder="Acme Security Corp"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Partnership Type *
                  </label>
                  <Input
                    required
                    value={partnershipData.partnershipType}
                    onChange={(e) => setPartnershipData({ ...partnershipData, partnershipType: e.target.value })}
                    className="bg-black/50 border-green-500/30 text-white"
                    placeholder="Reseller, Integrator, Training Partner, etc."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Additional Message
                  </label>
                  <textarea
                    value={partnershipData.message}
                    onChange={(e) => setPartnershipData({ ...partnershipData, message: e.target.value })}
                    className="w-full bg-black/50 border border-green-500/30 rounded-lg p-3 text-white resize-none"
                    placeholder="Tell us about your partnership interests..."
                    rows={4}
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-green-500 hover:bg-green-600 text-black font-semibold"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Submit Request
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowPartnershipForm(false)}
                    className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Footer */}
        <footer className="relative z-10 bg-black border-t border-green-500/20 py-12">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 mb-8">
              {/* Branding */}
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-emerald-600 rounded-lg flex items-center justify-center">
                    <span className="text-black font-bold font-mono">S</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-green-400 font-mono">Sycek</h3>
                    <p className="text-xs text-gray-500 font-mono">Cyber Intel & OSINT platform</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-4">
                  <a href="https://x.com/Sycek_osint" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300">
                    <Twitter className="w-5 h-5" />
                  </a>
                  <a href="https://www.linkedin.com/company/sycek-cyber-intelligence/" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300">
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a href="https://github.com/sycekosint" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300">
                    <Github className="w-5 h-5" />
                  </a>
                </div>
              </div>

              {/* Navigation */}
              <div>
                <h4 className="font-semibold mb-4 text-green-400">Platform</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li><Link to="/monitor" className="hover:text-green-400 transition-colors">Live Monitor</Link></li>
                  <li><Link to="/training" className="hover:text-green-400 transition-colors">Training Programs</Link></li>
                  <li><Link to="/api-docs" className="hover:text-green-400 transition-colors">API Documentation</Link></li>
                  <li><a href="https://community.sycek.io" target="_blank" rel="noopener noreferrer" className="hover:text-green-400 transition-colors">Community</a></li>
                  <li><Link to="/register" className="hover:text-green-400 transition-colors">Access Platform</Link></li>
                </ul>
              </div>

              {/* Products */}
              <div>
                <h4 className="font-semibold mb-4 text-green-400">Products</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li><Link to="/breachint" className="hover:text-green-400 transition-colors">BreachINT</Link></li>
                  <li><Link to="/walletint" className="hover:text-green-400 transition-colors">WalletINT</Link></li>
                  <li><Link to="/socint" className="hover:text-green-400 transition-colors">SOCINT</Link></li>
                  <li><Link to="/geoint" className="hover:text-green-400 transition-colors">GEOINT</Link></li>
                  <li><Link to="/aml-profiler" className="hover:text-green-400 transition-colors">AML Profiler</Link></li>
                  <li><Link to="/sycek-profiler" className="hover:text-green-400 transition-colors">Sycek Profiler</Link></li>
                </ul>
              </div>

              {/* Industries */}
              <div>
                <h4 className="font-semibold mb-4 text-green-400">Industries</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li><Link to="/industries/government" className="hover:text-green-400 transition-colors">Government</Link></li>
                  <li><Link to="/industries/law-enforcement" className="hover:text-green-400 transition-colors">Law Enforcement</Link></li>
                  <li><Link to="/industries/financial-services" className="hover:text-green-400 transition-colors">Financial Services</Link></li>
                  <li><Link to="/industries/cybersecurity" className="hover:text-green-400 transition-colors">Cybersecurity</Link></li>
                  <li><Link to="/industries/journalism" className="hover:text-green-400 transition-colors">Journalism</Link></li>
                  <li><Link to="/industries/aml" className="hover:text-green-400 transition-colors">Anti-Money Laundering</Link></li>
                </ul>
              </div>

              {/* Company */}
              <div>
                <h4 className="font-semibold mb-4 text-green-400">Company</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li><Link to="/privacy-policy" className="hover:text-green-400 transition-colors">Privacy Policy</Link></li>
                  <li><Link to="/terms-of-service" className="hover:text-green-400 transition-colors">Terms of Service</Link></li>
                  <li><Link to="/security" className="hover:text-green-400 transition-colors">Security</Link></li>
                  <li><Link to="/compliance" className="hover:text-green-400 transition-colors">Compliance</Link></li>
                  <li><button onClick={() => setShowPartnershipForm(true)} className="hover:text-green-400 transition-colors text-left">Partnerships</button></li>
                </ul>
              </div>

              {/* Stay Updated */}
              <div>
                <h4 className="font-semibold mb-4 text-green-400">Stay Updated</h4>
                <p className="text-gray-400 text-sm mb-4">
                  Get the latest in OSINT techniques and training delivered to your inbox.
                </p>
                <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-black/50 border-green-500/30 text-white text-sm"
                    required
                  />
                  <Button 
                    type="submit"
                    size="sm"
                    className="bg-green-500 hover:bg-green-600 text-black"
                    disabled={subscribed}
                  >
                    {subscribed ? <Check className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                  </Button>
                </form>
                <p className="text-xs text-gray-500 mt-2">
                  By submission, you agree to our <Link to="/privacy-policy" className="text-green-400 hover:underline">Privacy Policy</Link> and <Link to="/terms-of-service" className="text-green-400 hover:underline">Terms of Service</Link>.
                </p>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-green-500/20 pt-8 text-center">
              <p className="text-gray-400 text-sm">
                © 2026 Sycek Cyber Intel & OSINT. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>

      <CookieConsent />
    </>
  )
}

